import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { FONT_SIZE } from "../../theme/tokens";
import { type NurseInShift, NurseInShiftCreationSource } from "../../utils/models";
import { compareValues } from "../../utils/multiColumnSorting";
import { getShiftIndexByShiftTimes, getUniqueLocations } from "../../utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

type AddToScheduleDialogProps = {
	nurseShift: NurseInShift;
	date: DateTime;
	weekIndex: number;
	dayIndex: number;
	onClose: () => void;
};

/**
 * Build the scheduled shift from a punched actual shift. The actual is already a fully-formed
 * NurseInShift, so we take it as the base — every shift *detail* (times, job, role, location,
 * notes, …) carries over automatically, and any detail we add in the future does too.
 * We only touch the things that genuinely differ for a schedule entry:
 *  1. actual-only punch metadata, which must NOT leak onto the schedule, is stripped;
 *  2. `breaks` is reset to [] — the actual break *punches* aren't a planned-shift detail, matching
 *     the `prepareDataForAddNurse` default for new scheduled rows;
 *  3. the scheduling identity/state (fresh id + pending/published flags) is (re)set.
 */
const buildScheduledShiftFromActual = (actual: NurseInShift, isPending: boolean): NurseInShift => {
	const {
		missingPunchIn: _missingPunchIn,
		missingPunchOut: _missingPunchOut,
		cost: _cost,
		previousStartTime: _previousStartTime,
		previousEndTime: _previousEndTime,
		previousLocation: _previousLocation,
		previousNotes: _previousNotes,
		externalStartTime: _externalStartTime,
		externalEndTime: _externalEndTime,
		...shiftDetails
	} = actual;

	return {
		...shiftDetails,
		breaks: [],
		id: uuidv4(),
		isPublished: true,
		isPending,
		isPendingToDelete: false,
		isPendingToUpdate: false,
		isPendingToMove: false,
		isPendingToSwap: false,
		isDiscard: false,
	};
};

/**
 * Confirmation dialog for turning a punched-but-unscheduled actual shift into a scheduled shift,
 * mirroring the actual's details. No pickers — just confirm/cancel. The insert flows through the
 * standard `addNurse` pipeline (same route `QuickAddNurseDialog` uses).
 */
export const AddToScheduleDialog: FC<AddToScheduleDialogProps> = ({ nurseShift, date, weekIndex, dayIndex, onClose }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { isHospital, addNurseToShift, showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const {
		addNurse,
		isLoading: { addNurse: addNurseLoading },
	} = useHandleNurseChange();

	const nurseName = nurseShift.name;

	const locationOptions = useMemo<SystemAutoCompleteOption<string>[]>(() => {
		const names = getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location);
		return names.sort((a, b) => compareValues(a, b, customStringOrderMap)).map((loc) => ({ label: loc, value: loc }));
	}, [chosenDepartment.locations, customStringOrderMap]);

	const [selectedLocation, setSelectedLocation] = useState<string | null>(nurseShift.location ?? null);
	const [startTime, setStartTime] = useState<DateTime>(nurseShift.startTime);
	const [endTime, setEndTime] = useState<DateTime>(nurseShift.endTime);

	const performSave = useCallback(async () => {
		// Bucket follows the picked times (the scheduler may have edited them), same as QuickAddNurseDialog.
		const shiftNo = getShiftIndexByShiftTimes({ startTime, endTime });
		const nurseToAdd = buildScheduledShiftFromActual(nurseShift, isDraftMode ? true : isHospital);
		// Apply the scheduler's edits over the mirrored actual details.
		nurseToAdd.startTime = startTime;
		nurseToAdd.endTime = endTime;
		nurseToAdd.location = selectedLocation ?? undefined;
		const errorContext = { nurseId: nurseShift.nurseId, shiftId: nurseShift.id, date: date.toISODate() };

		if (!isDraftMode) {
			// In draft mode addNurse is a no-op (the change is picked up from local state by draft
			// auto-tracking), so persist only when not drafting; the local grid update runs in both cases.
			try {
				await addNurse({
					nurse: nurseToAdd,
					shiftIndex: shiftNo,
					dayNo: dayIndex,
					weekIndex,
					location: selectedLocation,
					isActual: false,
					source: NurseInShiftCreationSource.DuplicateActual,
					shiftTimes: { start: startTime, end: endTime },
					date,
				});
			} catch (e) {
				showErrorSnackbar(t("actual.addToSchedule.error", { nurseName }), errorContext, e);
				return;
			}
		}

		try {
			addNurseToShift(nurseToAdd, weekIndex, dayIndex, shiftNo, false, isDraftMode);
		} catch (e) {
			showErrorSnackbar(t("actual.addToSchedule.error", { nurseName }), errorContext, e);
			return;
		}

		track("add_to_schedule_from_actual", { nurse_id: nurseShift.nurseId, date: date.toISODate() });

		showSnackbar(t("nurse.added.success", { nurseName, day: startTime.toFormat("ccc"), shiftType: t(nurseShift.shiftType) }), "success");
		onClose();
	}, [
		nurseShift,
		date,
		weekIndex,
		dayIndex,
		isHospital,
		isDraftMode,
		selectedLocation,
		startTime,
		endTime,
		addNurse,
		addNurseToShift,
		track,
		showSnackbar,
		showErrorSnackbar,
		t,
		nurseName,
		onClose,
	]);

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth={false}
			data-testid="add-to-schedule-dialog"
			slotProps={{ paper: { sx: { borderRadius: 2, width: { xs: "45%", xl: "30%" }, height: "auto" } } }}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Typography sx={(theme) => ({ fontWeight: 600, fontSize: theme.typography.body1.fontSize, color: theme.palette.primary.main })}>
					{t("actual.addToSchedule.title", { nurseName, date: date.toFormat("MMMM d") })}
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px" }}>
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: "#4D5579" }}>{t("actual.addToSchedule.body")}</Typography>
				{locationOptions.length > 0 && (
					<SystemAutoComplete
						options={locationOptions}
						value={selectedLocation ? { label: selectedLocation, value: selectedLocation } : null}
						onChange={(_, option) => setSelectedLocation(option?.value ?? null)}
						textFieldProps={{ label: t("location"), placeholder: t("select.location"), inputSx: { width: 218 }, reserveHeightForHelperText: false }}
						dataTestId="add-to-schedule-location-input"
					/>
				)}
				<EditTimePopover
					jobId={nurseShift.jobId}
					startTime={startTime}
					endTime={endTime}
					onTimeChange={({ newStartTime, newEndTime }) => {
						setStartTime(newStartTime);
						setEndTime(newEndTime);
					}}
					onClose={() => {}}
					anchorEl={null}
					menuWrapper={false}
					hideActions
					hideFullShifts
					hidePatialShiftLengths
					disableAutoCompetePortal={false}
					sx={{ p: 0 }}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<SystemButton buttonVariant="secondary" onClick={onClose} data-testid="add-to-schedule-cancel-button">
					{t("cancel")}
				</SystemButton>
				<SystemButton loading={addNurseLoading} onClick={performSave} data-testid="add-to-schedule-submit-button">
					{t("save")}
				</SystemButton>
			</DialogActions>
		</Dialog>
	);
};
